import React from "react";
import { Box, Rating, Skeleton } from "@mui/material";
import { ColletteCheckbox } from "@collette-travel/collette-common-controls";
import { CUSTOMER_REVIEW_FIELD } from "@/config/unified-search-config";
import styles from "./styles.module.css";

type CountFilterOnChange = (data: Record<string, string>, title?: string) => void;

interface CountFilterOptionsProps {
  options: any[];
  fieldName: string;
  onChange?: CountFilterOnChange;
  value?: string[]; // Changed to array for multi-select support
  isLoading?: boolean;
  title?: string; // Add title prop
}

const CountFilterOptions: React.FC<CountFilterOptionsProps> = ({ options, fieldName, onChange, value, isLoading = false, title }) => {

  const isSelected = (filterValue: string) => value ? value.includes(filterValue) : false;

  // Custom sorting based on fieldName
  const sortedOptions = (() => {
    if (fieldName === "TourStyle") {
      // Sort by count descending for TourStyle
      return [...options].sort((a, b) => (b.count || 0) - (a.count || 0));
    }
    if (fieldName === "TourLengthDescription") {
      // Custom order for tour length: Up to 1 week, 1-2 weeks, 2 weeks +
      const lengthOrder = ["Up to 1 week", "1-2 weeks", "2 weeks +"];
      return [...options].sort((a, b) => {
        const aIndex = lengthOrder.indexOf(a.displayName);
        const bIndex = lengthOrder.indexOf(b.displayName);
        // If both items are in the custom order, sort by their position
        if (aIndex !== -1 && bIndex !== -1) {
          return aIndex - bIndex;
        }
        // If only one item is in the custom order, prioritize it
        if (aIndex !== -1) return -1;
        if (bIndex !== -1) return 1;
        // If neither item is in the custom order, maintain original order
        return 0;
      });
    }
    // Default: return original order
    return options;
  })();

  return (
    <Box className={styles.flexColumnGap}>
      {sortedOptions.map((filter) => {
        // During loading, show all current options to prevent UI jumping
        // When not loading, show option if it's selected OR has count > 0
        const shouldShow = isLoading ? true : (isSelected(filter.value) || filter.count > 0);

        return shouldShow && (
          <React.Fragment key={filter.value}>
            <ColletteCheckbox
              onChange={() => {
                // Always call onChange with data object for controlled state
                if (onChange) {
                  onChange({ [fieldName]: filter.value }, title);
                }
              }}
              name={fieldName}
              value={filter.value}
              checked={isSelected(filter.value)} // Use helper function for consistency
              disabled={isLoading} // Disable checkbox during loading
              // @ts-ignore
              label={
                fieldName !== CUSTOMER_REVIEW_FIELD
                  ? (
                    <>
                      {filter.displayName}
                      {isLoading ? <Skeleton sx={{ marginLeft: ".5rem", display: "inline-block" }} variant="rounded" width={20} /> : ` (${filter.count})`}

                    </>
                  )
                  : (
                    <>
                      {filter.displayName}
                      <Rating
                        value={Number(filter.displayName)}
                        max={Number(filter.displayName)}
                        precision={1}
                        readOnly
                        size="small"
                        data-testid={`search-page_feefo-review_${filter.displayName}_star-rating`}
                        sx={{ mx: ".5rem" }}
                      />
                      ({isLoading ? <Skeleton variant="text" width={20} sx={{ display: "inline-block" }} /> : filter.count})
                    </>
                  )
              }
              labelPlacement="end"
              testId={`count-filter-${title}-value-${filter.value}`}
              sx={{
                color: "primary.main",
                height: "3.4rem",
                width: "3.4rem"
              }}
            />
          </React.Fragment>
        );
      })}
    </Box>
  );
};

export default CountFilterOptions;
